// Mirror the formatting rules used in `backend/services/pdf_service.build_item_row`
// so the on-screen Quotation Detail view matches the generated PDF exactly.

import { joinWithHyphen } from './joinWithHyphen';

// Unit abbreviations — same map as the PDF
const UNIT_ABBREV = {
  piece: 'PCS', pieces: 'PCS', pc: 'PCS', pcs: 'PCS', nos: 'PCS',
  bundle: 'BDL', bundles: 'BDL', bdl: 'BDL',
  sheet: 'SHT', sheets: 'SHT', sht: 'SHT',
  set: 'SET', sets: 'SET',
  box: 'BOX', boxes: 'BOX',
  dozen: 'DZN', dz: 'DZN', dzn: 'DZN',
  kg: 'KG', kgs: 'KG',
  ltr: 'LTR', litre: 'LTR', liter: 'LTR',
  ml: 'ML',
  gms: 'GMS', gm: 'GMS',
  'running feet': 'RFT', rft: 'RFT',
  'sq ft': 'SQFT', sqft: 'SQFT', 'sq.ft': 'SQFT',
  pair: 'PR', pairs: 'PR', pr: 'PR',
  roll: 'ROLL', rolls: 'ROLL',
  pack: 'PK', packs: 'PK',
};

export function formatPdfUnit(item) {
  if (!item) return '-';
  const rateType = (item.rate_type || '').trim();
  let raw = rateType ? rateType.replace(/^per\s+/i, '') : '';
  if (!raw) {
    const u = String(item.unit || 'NOS').trim();
    const ul = u.toLowerCase();
    if (u === 'SHT') raw = 'sheet';
    else if (u === 'SQ FT') raw = 'sq ft';
    else if (u === 'NOS') raw = 'piece';
    else if (['rft', 'running feet', 'running_feet'].includes(ul)) raw = 'running feet';
    else raw = ul;
  }
  const key = raw.trim().toLowerCase();
  if (UNIT_ABBREV[key]) return UNIT_ABBREV[key];
  return (raw || '-').toUpperCase().slice(0, 5) || '-';
}

// Strip a brand prefix (and a trailing duplicate) from a description.
function stripBrandPrefix(desc, brand) {
  if (!desc || !brand) return desc;
  const dLow = desc.toLowerCase();
  const bLow = brand.toLowerCase();
  // Double prefix: "Brand Brand X" → "Brand X"
  if (dLow.startsWith(bLow + ' ' + bLow)) {
    return desc.slice(brand.length + 1).replace(/^[\s\-/.,]+/, '');
  }
  if (dLow.startsWith(bLow)) {
    return desc.slice(brand.length).replace(/^[\s\-/.,]+/, '');
  }
  return desc;
}

/**
 * Format the item description the same way the customer PDF does.
 * - Hides brand when `show_brand === false` (per-product Admin toggle)
 * - For Laminates / Veneers / Highlighters in customer view: uses
 *   product_code (+ finish / species) instead of brand
 * - Appends thickness for Plywood / Blockboard / MDF / Doors / WPC/PVC /
 *   Laminates / Highlighters where applicable
 *
 * @param {Object} item - quotation line item
 * @param {Object} opts - { hideBrands: bool, showBrandDescriptionItems: bool }
 */
export function formatPdfDescription(item, opts = {}) {
  if (!item) return '-';
  const hideBrands = opts.hideBrands !== false; // default true (customer view)
  const showBrandDescriptionItems = !!opts.showBrandDescriptionItems;
  const category = item.main_category || '';

  let description = (item.description || '').toString();
  let brand = (item.brand || '').toString();
  const productCode = (item.product_code || '').toString();
  const species = (item.species || item.veneer_type || '').toString();
  const thickness = (item.thickness || '').toString().trim();
  const finish = (item.finish || '').toString();

  // Per-product brand visibility toggle — Admin/cart can hide brand on
  // the CUSTOMER PDF only. Internal PDF (hideBrands=false) ALWAYS shows
  // the brand regardless of this flag, so store staff always see what
  // product a line corresponds to.
  const showBrandFlag = item.show_brand !== false;
  if (!showBrandFlag && hideBrands) {
    description = stripBrandPrefix(description, brand);
    brand = '';
  }

  const descBeforeStrip = description;

  // Defensive: remove duplicate brand at start
  if (brand) {
    description = stripBrandPrefix(description, brand);
    if (!description.trim()) description = descBeforeStrip;
  }

  // Veneers: clean leftover species / product_code from description
  if (category === 'Veneers') {
    if (brand) description = stripBrandPrefix(description, brand);
    if (productCode && description.includes(`(${productCode})`)) {
      description = description.replace(`(${productCode})`, '').trim();
    }
    if (species && description.toLowerCase().startsWith(species.toLowerCase())) {
      description = description.slice(species.length).replace(/^[\s\-]+/, '');
    }
  }

  // Per-item override: when user explicitly TICKED the "Show Brand"
  // toggle in cart, honour it even in customer view — for ANY category.
  // Earlier this was restricted to Lam/Ven/High; user feedback (iter 114)
  // expanded it to every category so Hardware/Adhesive/Plywood/etc. can
  // also surface the brand on the customer PDF when the staff opts in.
  const itemShowBrandOverride = showBrandFlag && !!brand;
  const hideBrandsForThisItem = hideBrands && !itemShowBrandOverride;

  if (hideBrandsForThisItem && ['Laminates', 'Veneers', 'Highlighters'].includes(category)) {
    if (category === 'Veneers') {
      const parts = [];
      if (productCode) parts.push(productCode);
      if (species) parts.push(`- ${species}`);
      if (parts.length) description = parts.join(' ');
    } else {
      // Customer PDF for Laminates/Highlighters when brand is hidden.
      // Format: DESCRIPTION - CODE - THICKNESS (each part optional).
      // `finish` field holds the user-typed Description in the iter114
      // Quick Add. Falls back to description_text for safety.
      const desc = (item.description_text || finish || '').toString().trim();
      description = joinWithHyphen(desc, productCode, thickness);
      if (!description) description = item.description || '-';
    }
  } else {
    // Internal (or non Lam/Ven/High) view
    if (category === 'Veneers') {
      const parts = [];
      if (productCode) parts.push(productCode);
      if (brand) parts.push(`- ${brand}`);
      if (species) parts.push(species);
      if (parts.length) description = parts.join(' ');
    } else if (['Laminates', 'Highlighters'].includes(category)) {
      // Internal PDF: BRAND - DESCRIPTION - CODE - THICKNESS. Brand stays
      // first because it's the canonical sort key for store staff.
      const desc = (item.description_text || finish || '').toString().trim();
      description = joinWithHyphen(brand, desc, productCode, thickness);
      if (!description) description = item.description || '-';
    }
  }

  // Plywood-family items with brand_description (the spanned header row):
  // show ONLY thickness in the description cell.
  if (showBrandDescriptionItems && ['Plywood', 'Blockboard', 'MDF', 'Doors', 'WPC/PVC'].includes(category)) {
    if (item.brand_description) {
      if (thickness) description = thickness;
      else if (!description) description = item.size || '-';
    }
  }

  // Always append thickness to Plywood / Blockboard / MDF / Doors / WPC/PVC
  if (['Plywood', 'Blockboard', 'MDF', 'Doors', 'WPC/PVC'].includes(category) && thickness) {
    if (!description.trim() || description.trim() === '-') {
      description = brand ? `${brand} - ${thickness}` : thickness;
    } else if (!description.toUpperCase().includes(thickness.toUpperCase())) {
      description = `${description} - ${thickness}`;
    }
  }

  // Hardware: prepend brand if it's not already shown (and brand isn't hidden)
  if (category === 'Hardware' && brand && !description.toLowerCase().startsWith(brand.toLowerCase())) {
    description = `${brand} ${description}`;
  }

  return description.trim() || '-';
}

// Format a percent discount: 40.0 → "40%", 12.5 → "12.5%", 0 → ""
export function fmtPctDiscount(v) {
  const f = parseFloat(v);
  if (isNaN(f) || f === 0) return '';
  return `${f}%`;
}

/**
 * Compute the per-unit NET RATE after discount for sqft / running-feet
 * items. Mirrors the PDF service's override at pdf_service.py:533–562 so
 * the on-screen viewer shows the SAME number the PDF prints (e.g., ₹140
 * per sq ft after 20% off ₹175), instead of the per-sheet net_rate from
 * the database.
 *
 * For non-sqft items (Laminates, Hardware, etc.) the original
 * `item.net_rate` is returned untouched.
 */
export function pdfStyleNetRate(item) {
  if (!item) return 0;
  const rateType = String(item.rate_type || '').toLowerCase();
  const isPerUnitRate = rateType === 'per sq ft' || rateType === 'per running feet';
  const baseRate = Number(item.rate || 0);
  if (!isPerUnitRate || !baseRate) return Number(item.net_rate || 0);

  const category = item.main_category || '';
  // Iter 115: cascading discount supported on every category — match
  // the runtime `CASCADING_CATEGORIES` set so the PDF/screen agree.
  const CASCADE_CATS = new Set(['Plywood', 'Blockboard', 'Laminates', 'Doors', 'WPC/PVC', 'Veneers', 'Highlighters', 'MDF', 'ACP', 'Hardware', 'Adhesives', 'Others']);
  let discFactor = 1.0;
  let rupeeSub = 0;  // iter 100 — rupee-mode cascading per-unit
  const cascading = String(item.cascading_discount || '').trim();
  if (cascading && CASCADE_CATS.has(category)) {
    const cascMode = (item.cascading_discount_type || 'percent').toLowerCase();
    for (const tok of cascading.split(/[+\s]+/)) {
      const p = parseFloat(tok);
      if (isNaN(p) || p <= 0) continue;
      if (cascMode === 'rupees') rupeeSub += p;
      else discFactor *= (1 - p / 100);
    }
  } else {
    const dv = parseFloat(item.discount_value || 0);
    if (dv > 0) {
      if (item.discount_type === 'percent') {
        discFactor = 1 - dv / 100;
      } else {
        // Amount-style discount on a per-unit rate — derive factor from
        // saved net_rate / calculated_rate if available.
        const calc = parseFloat(item.calculated_rate || 0);
        const nr = parseFloat(item.net_rate || 0);
        if (calc > 0) discFactor = nr / calc;
      }
    }
  }
  return Math.max(0, baseRate * discFactor - rupeeSub);
}

/**
 * Format the additional-discount LABEL with % in brackets when the discount
 * type was percent. Used in BOTH the totals strip of the on-screen viewer
 * and the PDF.
 *   percent type with value 5 → "Additional Discount (5%)"
 *   percent type with value 12.5 → "Additional Discount (12.5%)"
 *   rupees type or value missing → "Additional Discount"
 *
 * iter-133 — renamed label from "Special Discount" to "Additional
 * Discount". Underlying fields (`overall_discount_type` /
 * `overall_discount_value` / `overall_discount_amount`) are UNCHANGED.
 * Export name kept as `specialDiscountLabel` to avoid touching every
 * caller.
 */
export function specialDiscountLabel(discountType, discountValue) {
  const dv = parseFloat(discountValue);
  if (discountType === 'percent' && !isNaN(dv) && dv > 0) {
    return `Additional Discount (${dv}%)`;
  }
  return 'Additional Discount';
}

/**
 * Format the cascading-discount label for the DISC column. Mirrors the
 * pdf_service.format_cascading_disc_label helper exactly.
 *   percent → '10%+5%+3%'
 *   rupees  → 'Rs.25+Rs.10'
 */
export function formatCascadingLabel(cascadingDisc, mode = 'percent') {
  const tokens = String(cascadingDisc || '')
    .split(/[+\s,]+/)
    .map(t => parseFloat(t))
    .filter(n => !isNaN(n) && n > 0);
  if (!tokens.length) return cascadingDisc || 'NET';
  const isRupees = String(mode || 'percent').toLowerCase() === 'rupees';
  return tokens
    .map(f => {
      const s = Number.isInteger(f) ? String(f) : String(f);
      return isRupees ? `Rs.${s}` : `${s}%`;
    })
    .join('+');
}

/**
 * Round an INR amount to a whole rupee. Used for the Grand Total cell
 * (user wants no decimals shown — "If a percentage is put, show the
 * amount rounded off").
 */
export function roundINR(amount) {
  return Math.round(Number(amount || 0));
}
